import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from '../../types';
import {
  canSeeUsersTab,
  canSeeOrganizationsTab,
  canSeeFormsTab,
  canSeeProgramsTab,
  canSeeGroupsTab,
  isOrgAdmin,
  canSeeSitemap
} from '../../utils/roleUtils';

interface SidebarProps {
  user: User;
  activeTab: string;
  isMobileMenuOpen: boolean;
  onTabChange: (tab: string) => void;
  onSignOut: () => void;
  onCloseMobileMenu?: () => void;
}

interface TabItem {
  id: string;
  label: string;
  canShow: boolean;
  isFixed?: boolean; // For tabs that shouldn't be reordered (like welcome and sign out)
}

export function Sidebar({
  user,
  activeTab,
  isMobileMenuOpen,
  onTabChange,
  onSignOut,
  onCloseMobileMenu
}: SidebarProps) {
  const { t, i18n } = useTranslation();
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = useState<string | null>(null);
  // Navigation drawer is always open, no state needed
  
  const isRTL = i18n.language === 'he';
  const [isMobile, setIsMobile] = useState(false);

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Define all available tabs - wrapped in useMemo to recalculate when language changes
  const availableTabs = useMemo((): TabItem[] => [
    { id: 'dashboard', label: t('navigation.dashboard'), canShow: true },
    { id: 'users', label: t('navigation.userManagement'), canShow: canSeeUsersTab(user) },
    { id: 'organizations', label: t('navigation.organizations'), canShow: canSeeOrganizationsTab(user) },
    { id: 'programs', label: t('navigation.programs'), canShow: canSeeProgramsTab(user) },
    { id: 'groups', label: t('navigation.groups'), canShow: canSeeGroupsTab(user) },
    { id: 'projects', label: t('navigation.projects'), canShow: true },
    { id: 'calendar', label: t('navigation.calendar'), canShow: true },
    { id: 'tasks', label: t('navigation.tasks') || 'Tasks', canShow: true },
    { id: 'forms', label: t('navigation.forms'), canShow: canSeeFormsTab(user) }
  ], [t, i18n.language, user]);

  // Initialize tab order from localStorage or default
  const [tabOrder, setTabOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem('sidebarTabOrder');
    if (saved) {
      return JSON.parse(saved);
    }
    // Default order - will be updated by useEffect
    return ['dashboard', 'users', 'organizations', 'programs', 'groups', 'projects', 'calendar', 'tasks', 'forms'];
  });

  // Update tab order when user permissions change
  useEffect(() => {
    const currentTabs = availableTabs.filter(tab => tab.canShow).map(tab => tab.id);
    const savedOrder = tabOrder.filter(id => currentTabs.includes(id));
    const newTabs = currentTabs.filter(id => !savedOrder.includes(id));
    if (newTabs.length > 0) {
      const updatedOrder = [...savedOrder, ...newTabs];
      setTabOrder(updatedOrder);
      localStorage.setItem('sidebarTabOrder', JSON.stringify(updatedOrder));
    }
  }, [user, availableTabs, tabOrder]);

  const handleDragStart = (e: React.DragEvent, tabId: string) => {
    setDraggedItem(tabId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.innerHTML);
    (e.target as HTMLElement).style.opacity = '0.4';
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.target as HTMLElement).style.opacity = '1';
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (tabId: string) => {
    setDragOverItem(tabId);
  };

  const handleDrop = (e: React.DragEvent, dropTabId: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedItem || draggedItem === dropTabId) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    const newOrder = [...tabOrder];
    const draggedIndex = newOrder.indexOf(draggedItem);
    const dropIndex = newOrder.indexOf(dropTabId);

    // Remove dragged item and insert at new position
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(dropIndex, 0, draggedItem);

    setTabOrder(newOrder);
    localStorage.setItem('sidebarTabOrder', JSON.stringify(newOrder));
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const orderedTabs = tabOrder
    .map(id => availableTabs.find(tab => tab.id === id))
    .filter((tab): tab is TabItem => tab !== undefined && tab.canShow);

  return (
    <>
      {/* Navigation Drawer - Always Open */}
      <div 
        className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}
        style={{
          position: 'fixed',
          top: 0, // Start from top
          // On mobile, let CSS handle the positioning (left: -280px by default, left: 0 when mobile-open)
          // On desktop, always show (left: 0 for LTR, right: 0 for RTL)
          ...(isMobile ? {} : (isRTL ? { right: 0 } : { left: 0 })),
          height: '100vh', // Full height
          width: '280px',
          zIndex: 1001, // Above AppBar (zIndex: 1000)
          overflowY: 'auto'
        }}
        onClick={(e) => {
          // Prevent clicks inside sidebar from closing the menu
          e.stopPropagation();
        }}
      >
        {/* Logo at the top */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e9ecef',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'white'
        }}>
          <img 
            src="/logo.png" 
            alt="SocioSync" 
            style={{
              height: '40px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </div>

        {/* Mobile Close Button */}
        {onCloseMobileMenu && (
          <div className="mobile-close-button-container">
            <button
              onClick={onCloseMobileMenu}
              className="mobile-close-button"
              aria-label="Close menu"
            >
              ×
            </button>
          </div>
        )}
        <nav>
          <ul className="sidebar-nav">
            {/* Draggable tabs - hidden drag indicators */}
            {orderedTabs.map((tab) => (
              <li
                key={tab.id}
                draggable
                onDragStart={(e) => handleDragStart(e, tab.id)}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDragEnter={() => handleDragEnter(tab.id)}
                onDrop={(e) => handleDrop(e, tab.id)}
                style={{
                  transition: 'all 0.2s ease',
                  borderTop: dragOverItem === tab.id && draggedItem !== tab.id ? '2px solid #007acc' : '2px solid transparent',
                  opacity: draggedItem === tab.id ? 0.4 : 1
                }}
              >
                <button 
                  onClick={() => {
                    onTabChange(tab.id);
                    // Close mobile menu when a tab is clicked on mobile
                    if (onCloseMobileMenu) {
                      // Use media query check or just close - CSS will handle visibility
                      const isMobile = window.matchMedia('(max-width: 768px)').matches;
                      if (isMobile) {
                        onCloseMobileMenu();
                      }
                    }
                  }}
                  className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                  style={{
                    width: '100%',
                    pointerEvents: draggedItem ? 'none' : 'auto'
                  }}
                >
                  {tab.label}
                </button>
              </li>
            ))}

            {/* Fixed sign out section */}
            <li className="signout-nav-item">
              <button 
                onClick={onSignOut}
                className="sign-out-nav-btn"
              >
                {t('navigation.signOut')}
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
}


